import { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import {
  Database, Copy, RefreshCw, Loader2, Plus, Trash2, UserCircle2, Pencil,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCH_OPTIONS = [
  { code: 'C30', label: 'C30 Birmingham' },
  { code: 'E70', label: 'E70 West Brom' },
];

const NewGrantForm = ({ candidateUsers, onCreated, onCancel }) => {
  const [userId, setUserId] = useState('');
  const [allBranches, setAllBranches] = useState(true);
  const [branches, setBranches] = useState([]);
  const [settingsAccess, setSettingsAccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleBranch = (code) => {
    setBranches((prev) => (prev.includes(code) ? prev.filter((b) => b !== code) : [...prev, code]));
  };

  const handleCreate = async () => {
    if (!userId) {
      toast.error('Select a user');
      return;
    }
    if (!allBranches && branches.length === 0) {
      toast.error('Select at least one branch, or switch to All branches');
      return;
    }
    setSaving(true);
    try {
      await api.post(`${API}/admin/integrations/mcp-data-access/grants`, {
        user_id: userId,
        branches: allBranches ? ['all'] : branches,
        settings_access: settingsAccess,
      });
      toast.success('Grant created');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create grant');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3" data-testid="mcp-data-new-grant-form">
      <div>
        <label className="text-xs font-semibold text-gray-700">User</label>
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="w-full mt-1 text-sm rounded-md border border-gray-200 px-3 py-2 bg-white"
          data-testid="mcp-data-new-grant-user-select"
        >
          <option value="">Select a dashboard user…</option>
          {candidateUsers.map((u) => (
            <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.email || u.username})</option>
          ))}
        </select>
      </div>
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-gray-700">All branches</label>
        <Switch checked={allBranches} onCheckedChange={setAllBranches} data-testid="mcp-data-new-grant-all-branches-switch" />
      </div>
      {!allBranches && (
        <div className="flex gap-2" data-testid="mcp-data-new-grant-branch-picker">
          {BRANCH_OPTIONS.map((b) => (
            <button
              key={b.code}
              type="button"
              onClick={() => toggleBranch(b.code)}
              className={`text-xs px-3 py-1.5 rounded-full border font-medium ${
                branches.includes(b.code) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'
              }`}
              data-testid={`mcp-data-new-grant-branch-${b.code}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <label className="text-xs font-semibold text-gray-700">Settings access</label>
          <p className="text-[11px] text-gray-400">Lets this grant see app config/settings collections (never secrets/passwords, regardless).</p>
        </div>
        <Switch checked={settingsAccess} onCheckedChange={setSettingsAccess} data-testid="mcp-data-new-grant-settings-access-switch" />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="mcp-data-new-grant-cancel-btn">Cancel</Button>
        <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="mcp-data-new-grant-create-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
          Add Grant
        </Button>
      </div>
    </div>
  );
};

const GrantRow = ({ grant, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [editingBranches, setEditingBranches] = useState(false);
  const [allBranches, setAllBranches] = useState((grant.branches || []).includes('all'));
  const [branches, setBranches] = useState((grant.branches || []).includes('all') ? [] : (grant.branches || []));

  const openBranchEditor = () => {
    setAllBranches((grant.branches || []).includes('all'));
    setBranches((grant.branches || []).includes('all') ? [] : (grant.branches || []));
    setEditingBranches(true);
  };

  const toggleBranch = (code) => {
    setBranches((prev) => (prev.includes(code) ? prev.filter((b) => b !== code) : [...prev, code]));
  };

  const handleSaveBranches = async () => {
    if (!allBranches && branches.length === 0) {
      toast.error('Select at least one branch, or switch to All branches');
      return;
    }
    setBusy(true);
    try {
      await api.put(`${API}/admin/integrations/mcp-data-access/grants/${grant.id}`, {
        branches: allBranches ? ['all'] : branches,
      });
      toast.success('Branches updated');
      setEditingBranches(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update grant');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async () => {
    setBusy(true);
    try {
      await api.put(`${API}/admin/integrations/mcp-data-access/grants/${grant.id}`, { active: !grant.active });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update grant');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleSettingsAccess = async () => {
    setBusy(true);
    try {
      await api.put(`${API}/admin/integrations/mcp-data-access/grants/${grant.id}`, { settings_access: !grant.settings_access });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update grant');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleWriteAccess = async () => {
    const turningOn = !grant.write_access;
    if (turningOn && !window.confirm(
      `Let Claude WRITE to the live database for "${grant.user_name}"?\n\n`
      + 'Claude will always show a full plan and wait for them to type a confirmation code before anything is changed, '
      + 'every write is logged with a copy of what it replaced, and it can be undone. Users, permissions and security data can never be written.'
    )) return;
    setBusy(true);
    try {
      await api.put(`${API}/admin/integrations/mcp-data-access/grants/${grant.id}`, { write_access: turningOn });
      toast.success(turningOn ? 'Write to Database switched on' : 'Write to Database switched off');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update grant');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove Dashboard Data Access for "${grant.user_name}"? They will lose access immediately.`)) return;
    setBusy(true);
    try {
      await api.delete(`${API}/admin/integrations/mcp-data-access/grants/${grant.id}`);
      toast.success('Grant removed');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete grant');
    } finally {
      setBusy(false);
    }
  };

  const branchLabel = (grant.branches || []).includes('all') ? 'All branches' : (grant.branches || []).join(', ');

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-3 space-y-2" data-testid={`mcp-data-grant-row-${grant.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCircle2 className="w-4 h-4 text-gray-400" />
          <p className="text-sm font-semibold text-gray-800">{grant.user_name}</p>
          <span className="text-[11px] text-gray-400">{grant.user_email}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${grant.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
            {grant.active ? 'Active' : 'Disabled'}
          </span>
        </div>
        <Button
          variant="outline" size="sm" onClick={handleDelete} disabled={busy}
          className="text-red-600 border-red-200 hover:bg-red-50"
          data-testid={`mcp-data-grant-delete-btn-${grant.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <div className="flex items-center gap-1.5" data-testid={`mcp-data-grant-branches-${grant.id}`}>
          <span>Branches: {branchLabel}</span>
          {!editingBranches && (
            <button onClick={openBranchEditor} data-testid={`mcp-data-grant-edit-branches-btn-${grant.id}`}>
              <Pencil className="w-3 h-3 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        <span>Last connected: {grant.last_used_at ? new Date(grant.last_used_at).toLocaleString('en-GB') : 'Not connected yet'}</span>
      </div>

      {editingBranches && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-2.5 space-y-2" data-testid={`mcp-data-grant-branch-editor-${grant.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-gray-600">All branches</span>
            <Switch checked={allBranches} onCheckedChange={setAllBranches} data-testid={`mcp-data-grant-all-branches-switch-${grant.id}`} />
          </div>
          {!allBranches && (
            <div className="flex gap-2" data-testid={`mcp-data-grant-branch-picker-${grant.id}`}>
              {BRANCH_OPTIONS.map((b) => (
                <button
                  key={b.code}
                  type="button"
                  onClick={() => toggleBranch(b.code)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium ${
                    branches.includes(b.code) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'
                  }`}
                  data-testid={`mcp-data-grant-branch-${b.code}-${grant.id}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditingBranches(false)} disabled={busy} data-testid={`mcp-data-grant-branches-cancel-btn-${grant.id}`}>Cancel</Button>
            <Button size="sm" onClick={handleSaveBranches} disabled={busy} data-testid={`mcp-data-grant-branches-save-btn-${grant.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-1 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium text-gray-600">Settings access</span>
          <Switch checked={!!grant.settings_access} onCheckedChange={handleToggleSettingsAccess} disabled={busy} data-testid={`mcp-data-grant-settings-access-switch-${grant.id}`} />
          <span className="ml-3 text-[11px] font-bold text-red-700">Write to Database</span>
          <Switch
            checked={!!grant.write_access}
            onCheckedChange={handleToggleWriteAccess}
            disabled={busy}
            className="data-[state=checked]:bg-red-600"
            data-testid={`mcp-data-grant-write-access-switch-${grant.id}`}
          />
        </div>
        <Button variant="outline" size="sm" onClick={handleToggleActive} disabled={busy} data-testid={`mcp-data-grant-active-toggle-btn-${grant.id}`}>
          {grant.active ? 'Disable' : 'Enable'}
        </Button>
      </div>
    </div>
  );
};

export const McpDataAccessSettings = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [candidateUsers, setCandidateUsers] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [writes, setWrites] = useState([]); // recent Claude database writes (audit)

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const [statusRes, usersRes, writesRes] = await Promise.all([
        api.get(`${API}/admin/integrations/mcp-data-access`),
        api.get(`${API}/admin/integrations/mcp-data-access/users`),
        api.get(`${API}/admin/integrations/mcp-data-access/writes?limit=15`).catch(() => ({ data: { writes: [] } })),
      ]);
      setStatus(statusRes.data);
      setCandidateUsers(usersRes.data.users || []);
      setWrites(writesRes.data.writes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load MCP status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (loading && !status) {
    return (
      <div className="rounded-xl border bg-white p-6 flex items-center justify-center" data-testid="mcp-data-access-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const grants = status?.grants || [];
  const grantedUserIds = new Set(grants.map((g) => g.user_id));
  const availableUsers = candidateUsers.filter((u) => !grantedUserIds.has(u.id));

  return (
    <div className="rounded-xl border bg-white p-4" data-testid="mcp-data-access-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-bold text-gray-900">Dashboard Data Access (MCP)</h3>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading} data-testid="mcp-data-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
        Gives Claude access — read-only by default; a super admin can switch on Write to Database per person — to this app's live DATABASE (orders, transport, sales, CRM cache, users,
        etc — never the codebase, that's the separate card above) via OAuth: each person below logs into the
        dashboard as themselves and approves a consent screen, no key to copy/paste. Passwords, encrypted API
        credentials and session tokens are never accessible through this connector, regardless of any grant's
        settings. Each person can be scoped to specific branches and can independently see or be blocked from
        app config/settings-type collections.
      </p>

      <div>
        <label className="text-xs font-semibold text-gray-700">MCP Server URL</label>
        <div className="flex gap-2 mt-1">
          <input
            readOnly value={status?.mcp_url || ''}
            className="flex-1 font-mono text-sm rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700"
            data-testid="mcp-data-server-url-input"
          />
          <Button variant="outline" size="sm" onClick={() => copyToClipboard(status?.mcp_url || '', 'MCP Server URL')} data-testid="mcp-data-copy-url-btn">
            <Copy className="w-3.5 h-3.5" />
          </Button>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          Add this as a Custom Connector URL in Claude — anyone with an active grant below can then log in and
          approve it for themselves.
        </p>
      </div>

{writes.length > 0 && (
        <div className="pt-4 mt-4 border-t border-gray-100" data-testid="mcp-data-recent-writes">
          <label className="text-xs font-semibold text-gray-700">Recent database writes by Claude</label>
          <div className="mt-2 rounded-lg border border-gray-100 divide-y divide-gray-100 max-h-56 overflow-y-auto">
            {writes.map((w) => (
              <div key={w.write_id} className="px-3 py-2 text-[11px] text-gray-600 flex items-start justify-between gap-3" data-testid={`mcp-data-write-${w.write_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate">{w.summary}</p>
                  <p className="text-gray-400">{w.by} · {(w.collections || []).join(', ')}{w.problem ? ` · stopped early: ${w.problem}` : ''}</p>
                </div>
                <div className="text-right flex-shrink-0 text-gray-400">
                  <p>{w.at ? new Date(w.at).toLocaleString('en-GB') : ''}</p>
                  {w.undone && <p className="text-amber-600 font-semibold">Undone</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="pt-4 mt-4 border-t border-gray-100 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-gray-700">Grants</label>
          {!showNewForm && (
            <Button size="sm" onClick={() => setShowNewForm(true)} data-testid="mcp-data-add-grant-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Grant
            </Button>
          )}
        </div>

        {showNewForm && (
          <NewGrantForm
            candidateUsers={availableUsers}
            onCreated={() => { setShowNewForm(false); fetchStatus(); }}
            onCancel={() => setShowNewForm(false)}
          />
        )}

        {grants.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="mcp-data-grants-empty">No grants yet.</p>
        ) : (
          <div className="space-y-2" data-testid="mcp-data-grants-list">
            {grants.map((g) => (
              <GrantRow key={g.id} grant={g} onChanged={fetchStatus} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default McpDataAccessSettings;
